import React, { useState } from "react";
import { Card, Col, Row, Space, Spin, Table, Pagination, Input } from "antd";
import { ColumnType } from "antd/es/table";

// New type for action buttons
type ActionButton = React.ReactNode;

interface TableCardProps {
  title: string;
  columns: ColumnType<any>[];
  dataSource: any[];
  baseData: any[];
  isLoading?: boolean;
  actionButtons?: ActionButton[]; // Array of buttons/components to render in header
  currentPage_: number;
  pageSize_: number;
  pageTotal: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  expandableConfig?: {
    // Replace direct expandedRowRender with full config
    expandedRowRender: (record: any) => React.ReactNode;
    expandRowByClick?: boolean;
    rowExpandable?: (record: any) => boolean;
  };
  rowKey?: string; // Add this prop
  onChange?: (pagination: any, filters: any, sorter: any) => void;
}

const TableCard: React.FC<TableCardProps> = ({
  title,
  columns,
  dataSource,
  baseData,
  isLoading = false,
  actionButtons = [],
  currentPage_,
  pageSize_,
  pageTotal,
  setCurrentPage,
  setPageSize,
  expandableConfig,
  rowKey = "run_name",
  onChange,
}) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [filterTable, setFilterTable] = useState<any[]>(null);

  const handlePaginationChange = (page: number, pageSize?: number) => {
    if (pageSize && pageSize !== pageSize_) {
      setCurrentPage(1);
      setPageSize(pageSize);
    } else {
      setCurrentPage(page);
    }
  };

  const search = (value: string) => {
    if (!value) {
      setFilterTable(null);
      return;
    }

    const filterTable = baseData.filter((o) =>
      Object.keys(o).some((k) =>
        String(o[k]).toLowerCase().includes(value.toLowerCase())
      )
    );

    setFilterTable(filterTable);
  };

  const handleExpand = (expanded: boolean, record: any) => {
    const key = record[rowKey];

    setExpandedRowKeys((prev) => {
      const newKeys = expanded ? [...prev, key] : prev.filter((k) => k !== key);
      return newKeys;
    });
  };

  return (
    <Card className="applications-wrapper">
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Row>
          <Col>
            <h1>{title}</h1>
          </Col>
        </Row>
        <Row justify="space-between" align="middle">
          <Col span={8}>
            <Input.Search
              placeholder="Search..."
              enterButton
              onSearch={search}
            />
          </Col>
          <Col>
            <Space>{actionButtons}</Space>
          </Col>
        </Row>
        {isLoading ? (
          <Spin
            size="large"
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "20px",
            }}
          />
        ) : (
          <div
            style={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              height: "calc(100vh - 250px)",
            }}>
            <div
              style={{
                overflowX: "auto",
                overflowY: "auto",
                flexGrow: 1,
                paddingBottom: "50px", // Reserve space for the pagination
              }}>
              <Table
                scroll={{ x: true }}
                dataSource={filterTable == null ? dataSource : filterTable}
                rowKey={rowKey}
                pagination={false}
                childrenColumnName="no_children"
                key={`table-${expandedRowKeys.join("-")}`}
                expandable={
                  expandableConfig
                    ? {
                        ...expandableConfig,
                        expandedRowKeys,
                        onExpand: handleExpand,
                        rowExpandable: (record) => {
                          const expandable =
                            expandableConfig.rowExpandable?.(record) ?? true;
                          return expandable;
                        },
                      }
                    : undefined
                }
                components={{
                  header: {
                    cell: (props: any) => (
                      <th
                        {...props}
                        style={{
                          position: "sticky",
                          top: 0,
                          background: "white",
                          zIndex: 0,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          padding: "8px",
                        }}
                      />
                    ),
                  },
                  body: {
                    cell: (props: any) => (
                      <td
                        {...props}
                        style={{
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          padding: "8px",
                          cursor: "default",
                        }}
                      />
                    ),
                    row: (props: any) => (
                      <tr
                        {...props}
                        style={{
                          cursor: "default",
                        }}
                      />
                    ),
                  },
                }}
                columns={columns}
                onChange={onChange}
              />
            </div>
            <div
              style={{
                bottom: 0,
                padding: "8px 0",
                zIndex: 1,
              }}>
              <Row justify="end">
                <Pagination
                  current={currentPage_}
                  pageSize={pageSize_}
                  total={pageTotal}
                  showSizeChanger
                  pageSizeOptions={["10", "20", "50", "100"]}
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`
                  }
                  onChange={handlePaginationChange}
                />
              </Row>
            </div>
          </div>
        )}
      </Space>
    </Card>
  );
};

export default TableCard;
